import { supabase } from './supabase';
import { isNativeApp } from './nativeShell';

/**
 * Guard against any Stripe redirect being triggered inside the iOS or
 * Android Capacitor shells. Apple Guideline 3.1.1 forbids in-app paths
 * to external paid signup flows for digital subscriptions, so we hide
 * the UI in Plan.tsx; this is a defence-in-depth backstop so a future
 * caller (a settings page, an alert CTA, a deep link) can't accidentally
 * re-introduce the violation.
 */
function assertWebContext(action: string): void {
  if (isNativeApp()) {
    throw new Error(`${action} isn't available in the app.`);
  }
}

/**
 * Billing cycle for a tier. Annual = pay for 10 months, get 12 (~17% off).
 * Stored per-org via Stripe so the webhook can update the right interval.
 */
export type BillingCycle = 'monthly' | 'annual';

/**
 * Stripe price-ID catalog. These map our internal tier IDs to the Stripe
 * Price IDs created in the Stripe Dashboard. We use environment variables
 * so the same code works in test and live mode — Vite injects VITE_-prefixed
 * env vars at build time.
 *
 * Setting up the products in Stripe Dashboard:
 *  1. Stripe → Catalog → + Add product
 *  2. Solo: name "Safe and Sorted — Solo (1-3 phones)"
 *     - monthly recurring £19 GBP
 *     - yearly recurring £190 GBP  (= 10 months, save £38)
 *  3. Team: name "Safe and Sorted — Team (4-10 phones)"
 *     - monthly recurring £29 GBP
 *     - yearly recurring £290 GBP  (= 10 months, save £58)
 *  4. Site: name "Safe and Sorted — Site (11-25 phones)"
 *     - monthly recurring £39 GBP
 *     - yearly recurring £390 GBP  (= 10 months, save £78)
 *  5. Enterprise: leave for now — quote-only, no public Price ID needed
 *  6. Copy each price's price_id (starts with price_) into Netlify env vars.
 */
export const TIER_CATALOG: Record<
  'tier1' | 'tier2' | 'tier3',
  {
    priceId: string | undefined;
    yearlyPriceId: string | undefined;
    label: string;
    monthlyGbp: number;
    yearlyGbp: number;
    phones: string;
  }
> = {
  tier1: {
    priceId: import.meta.env.VITE_STRIPE_PRICE_TIER1 as string | undefined,
    yearlyPriceId: import.meta.env.VITE_STRIPE_PRICE_TIER1_YEARLY as string | undefined,
    // Renamed 'Solo' → 'Small Team' Aug 2026 for Apple Review Guideline 3.1.3(c)
    // (Enterprise Services) — Safe and Sorted is positioned as B2B for
    // small construction firms with 1–3 users, not individual consumers.
    label: 'Small Team',
    monthlyGbp: 19,
    yearlyGbp: 190,
    phones: '1–3 users',
  },
  tier2: {
    priceId: import.meta.env.VITE_STRIPE_PRICE_TIER2 as string | undefined,
    yearlyPriceId: import.meta.env.VITE_STRIPE_PRICE_TIER2_YEARLY as string | undefined,
    label: 'Team',
    monthlyGbp: 29,
    yearlyGbp: 290,
    phones: '4–10 users',
  },
  tier3: {
    priceId: import.meta.env.VITE_STRIPE_PRICE_TIER3 as string | undefined,
    yearlyPriceId: import.meta.env.VITE_STRIPE_PRICE_TIER3_YEARLY as string | undefined,
    label: 'Site',
    monthlyGbp: 39,
    yearlyGbp: 390,
    phones: '11–25 users',
  },
};

/** How much annual saves vs 12× monthly, in GBP. */
export function annualSavingGbp(tier: 'tier1' | 'tier2' | 'tier3'): number {
  const t = TIER_CATALOG[tier];
  return Math.max(0, t.monthlyGbp * 12 - t.yearlyGbp);
}

/**
 * Open the Stripe Checkout page for a given tier. Calls the
 * `stripe-checkout` Edge Function which creates the session server-side
 * (so the price IDs and secret key never leave the server).
 *
 * NOTE: this Edge Function isn't built yet — see TODO at bottom. For now the
 * client-side fallback redirects to a Stripe-hosted Payment Link if
 * VITE_STRIPE_PAYMENT_LINK_TIERn is set. Payment Links work without any
 * server code; they're the simplest possible path from "I want to subscribe"
 * to "I'm subscribed".
 */
/**
 * Best-effort lookup of the currently signed-in user's email. Used to
 * pre-fill the Stripe Checkout form so the customer record matches the
 * Supabase user. Returns null on any error so the checkout flow is never
 * blocked by a missing email — it just falls back to letting the customer
 * type one in (which is what got us into the email-mismatch trouble in
 * the first place, but is better than no checkout at all).
 */
async function getSignedInEmail(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getUser();
    return data.user?.email ?? null;
  } catch {
    return null;
  }
}

/**
 * Hardcoded Stripe Payment Link URLs. These are PUBLIC URLs — they're already
 * baked into the marketing site's pricing.html and visible to anyone reading
 * the page source — so no secret leaks by keeping them here too. Used as a
 * fallback when the VITE_STRIPE_PAYMENT_LINK_TIERn env vars aren't set on the
 * deploy environment, which is what was happening on the app subdomain on
 * 11 Jun 2026 (the "Opening checkout…" stuck button report). Env var takes
 * precedence so we can swap to test-mode links by setting them per-environment.
 */
const PAYMENT_LINK_FALLBACK: Record<'tier1' | 'tier2' | 'tier3', { monthly: string; yearly: string }> = {
  tier1: {
    monthly: 'https://buy.stripe.com/eVq6oJ9iW8yc5fPbdX18c00',
    yearly:  'https://buy.stripe.com/bJebJ3fHk5m0fUtci118c03',
  },
  tier2: {
    monthly: 'https://buy.stripe.com/dRm6oJ9iW7u87nX81L18c01',
    yearly:  'https://buy.stripe.com/bJe9AVbr4bKo6jTfud18c04',
  },
  tier3: {
    monthly: 'https://buy.stripe.com/fZu4gBgLo4hWeQpfud18c02',
    yearly:  'https://buy.stripe.com/28EcN70Mq01G8s14Pz18c05',
  },
};

export async function startCheckout(
  tier: 'tier1' | 'tier2' | 'tier3',
  cycle: BillingCycle = 'monthly',
): Promise<void> {
  assertWebContext('Subscription checkout');
  const envVar = (() => {
    if (cycle === 'annual') {
      if (tier === 'tier1') return import.meta.env.VITE_STRIPE_PAYMENT_LINK_TIER1_YEARLY as string | undefined;
      if (tier === 'tier2') return import.meta.env.VITE_STRIPE_PAYMENT_LINK_TIER2_YEARLY as string | undefined;
      if (tier === 'tier3') return import.meta.env.VITE_STRIPE_PAYMENT_LINK_TIER3_YEARLY as string | undefined;
      return undefined;
    }
    if (tier === 'tier1') return import.meta.env.VITE_STRIPE_PAYMENT_LINK_TIER1 as string | undefined;
    if (tier === 'tier2') return import.meta.env.VITE_STRIPE_PAYMENT_LINK_TIER2 as string | undefined;
    if (tier === 'tier3') return import.meta.env.VITE_STRIPE_PAYMENT_LINK_TIER3 as string | undefined;
    return undefined;
  })();

  const fallback = PAYMENT_LINK_FALLBACK[tier][cycle === 'annual' ? 'yearly' : 'monthly'];
  const paymentLink = envVar || fallback;

  if (paymentLink) {
    // Stripe Payment Link: pre-built hosted Checkout, no server code needed.
    // After payment, Stripe webhook → our Edge Function → updates the org.
    //
    // Pre-fill the customer email with the signed-in user's email so the
    // Stripe Customer record gets created with the SAME email as the
    // Supabase user. Without this, customers were typing a different email
    // at checkout and the webhook had no way to link the payment to their
    // app account (Marc's payment 11 Jun 2026 — paid as
    // admin@projectevolve.uk while logged into the app as mspt@hotmail.co.uk).
    // See https://docs.stripe.com/payment-links/url-parameters
    const userEmail = await getSignedInEmail();
    const url = userEmail
      ? `${paymentLink}?prefilled_email=${encodeURIComponent(userEmail)}`
      : paymentLink;
    window.location.href = url;
    return;
  }

  // Last-ditch fallback: ask the Edge Function for a Checkout session
  // (TODO: build). Pass cycle so the function can pick the right Price ID.
  // We shouldn't get here in practice since the hardcoded fallback covers
  // every tier/cycle combination — guard left in for forward-compatibility
  // if a future tier is introduced before its URL is wired up.
  const { data, error } = await supabase.functions.invoke('stripe-checkout', {
    body: { tier, cycle },
  });
  if (error) throw error;
  const url = (data as { url?: string } | null)?.url;
  if (!url) throw new Error('Could not start checkout — no URL returned.');
  window.location.href = url;
}

/**
 * Open the Stripe Customer Portal so the user can update their card,
 * change plan, or cancel. Same idea as startCheckout — Payment Link
 * has a fallback "Customer Portal Link" if set, otherwise we hit the
 * `stripe-portal` Edge Function.
 */
export async function openCustomerPortal(): Promise<void> {
  assertWebContext('The billing portal');
  const portalLink = import.meta.env.VITE_STRIPE_PORTAL_LINK as string | undefined;
  if (portalLink) {
    window.location.href = portalLink;
    return;
  }

  const { data, error } = await supabase.functions.invoke('stripe-portal', {
    body: {},
  });
  if (error) throw error;
  const url = (data as { url?: string } | null)?.url;
  if (!url) throw new Error('Could not open the billing portal.');
  window.location.href = url;
}

/** Returns the billing snapshot for an org — pulls from the columns
 *  populated by the stripe-webhook Edge Function. */
export interface BillingSnapshot {
  tier: 'tier1' | 'tier2' | 'tier3' | 'enterprise';
  status: 'unpaid' | 'trial' | 'active' | 'past_due' | 'cancelled' | 'paused';
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  gracePeriodEndsAt: string | null;
  stripeCustomerId: string | null;
  referredByPartnerCode: string | null;
}

export async function getBillingSnapshot(orgId: string): Promise<BillingSnapshot> {
  const { data, error } = await supabase
    .from('organisations')
    .select(
      'subscription_tier, subscription_status, subscription_current_period_end, subscription_cancel_at_period_end, grace_period_ends_at, stripe_customer_id, referred_by_partner_code',
    )
    .eq('id', orgId)
    .single();
  if (error) throw error;

  return {
    tier:              (data?.subscription_tier as BillingSnapshot['tier']) ?? 'tier1',
    status:            (data?.subscription_status as BillingSnapshot['status']) ?? 'unpaid',
    currentPeriodEnd:  (data?.subscription_current_period_end as string | null) ?? null,
    cancelAtPeriodEnd: !!data?.subscription_cancel_at_period_end,
    gracePeriodEndsAt: (data?.grace_period_ends_at as string | null) ?? null,
    stripeCustomerId:  (data?.stripe_customer_id as string | null) ?? null,
    referredByPartnerCode: (data?.referred_by_partner_code as string | null) ?? null,
  };
}

// TODO (post-launch): build `stripe-checkout` and `stripe-portal` Edge
// Functions to replace the Payment Link fallback. Payment Links work fine
// for v1; the Edge Functions become necessary when we want to:
//   - Pre-fill the customer email
//   - Attach metadata.org_id so the webhook can match on first event
//   - Add an explicit trial period via Checkout
//   - Apply promo codes from URL ?promo=
